import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCampaign,
  lintArm,
  listCampaigns,
  listVersions,
  restoreVersion,
  saveContent,
} from '@/lib/campaigns-api';
import type { CampaignStatus } from '@/lib/schemas';

/**
 * TanStack Query wrappers. Server state lives here and nowhere else — never mirrored into
 * `useState`, which would be a second cache that goes stale.
 *
 * Query keys are arrays with the parameters in them, so invalidating `['campaigns']` invalidates
 * every filtered list without needing to know which filters are active.
 */

export const keys = {
  campaigns: (filters: { status?: CampaignStatus; query?: string }) =>
    ['campaigns', filters] as const,
  campaign: (id: string) => ['campaign', id] as const,
  lint: (campaignId: string, armId: string, contactId?: string) =>
    ['lint', campaignId, armId, contactId ?? null] as const,
  versions: (campaignId: string, armId: string) => ['versions', campaignId, armId] as const,
};

export function useCampaigns(filters: { status?: CampaignStatus; query?: string }) {
  return useQuery({
    queryKey: keys.campaigns(filters),
    queryFn: () => listCampaigns(filters),
  });
}

export function useCampaign(campaignId: string) {
  return useQuery({
    queryKey: keys.campaign(campaignId),
    queryFn: () => getCampaign(campaignId),
  });
}

/**
 * Build, compile, lint and preview.
 *
 * `staleTime: 0` because the whole point is that it reflects the content as it is right now; the
 * debounce in the workspace controls how often it actually fires, not the cache.
 */
export function useLint(campaignId: string, armId: string | undefined, previewContactId?: string) {
  // Captured narrowed, so the queryFn closes over a `string` rather than relying on the reader
  // knowing that `enabled` guarantees it. Same guarantee, expressed in the type.
  const resolvedArmId = armId ?? '';

  return useQuery({
    queryKey: keys.lint(campaignId, resolvedArmId, previewContactId),
    queryFn: ({ signal }) => lintArm(campaignId, resolvedArmId, previewContactId, signal),
    enabled: armId !== undefined,
    staleTime: 0,
    // Keeps the previous findings on screen while the next run is in flight, so the drawer does not
    // empty and refill on every save — which reads as flicker rather than as progress.
    placeholderData: (previous) => previous,
  });
}

export function useVersions(campaignId: string, armId: string | undefined) {
  const resolvedArmId = armId ?? '';

  return useQuery({
    queryKey: keys.versions(campaignId, resolvedArmId),
    queryFn: () => listVersions(campaignId, resolvedArmId),
    enabled: armId !== undefined,
  });
}

export function useSaveContent(campaignId: string, armId: string | undefined) {
  const client = useQueryClient();
  const resolvedArmId = armId ?? '';

  return useMutation({
    mutationFn: (body: {
      html: string;
      subject: string;
      preheader: string | null;
      message: string | null;
    }) => saveContent(campaignId, resolvedArmId, body),

    onSuccess: () => {
      // The lint result and the version list are both downstream of a save. Invalidating rather
      // than optimistically updating, because the server computes things the client cannot —
      // the variant count, the byte size of the built artefact, the new version number.
      void client.invalidateQueries({ queryKey: ['lint', campaignId] });
      void client.invalidateQueries({ queryKey: ['versions', campaignId] });
      void client.invalidateQueries({ queryKey: keys.campaign(campaignId) });
    },
  });
}

export function useRestoreVersion(campaignId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (versionId: string) => restoreVersion(campaignId, versionId),

    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.campaign(campaignId) });
      void client.invalidateQueries({ queryKey: ['lint', campaignId] });
      void client.invalidateQueries({ queryKey: ['versions', campaignId] });
    },
  });
}
